import { useEffect, useState } from 'react'
import logo from '../assets/conetec-logo.svg'
import { pickSignInLang, SIGN_IN_COPY, SIGN_IN_LANGS, type SignInLang } from '../lib/signInCopy'
import { isUnsubscribeToken, UNSUBSCRIBE, unsubscribeByToken } from '../lib/communication'
import { UnsubscribeBody, type UnsubscribePhase as Phase } from '../components/UnsubscribeBody'

/**
 * #/unsubscribe/<token> — stop the Monday email without signing in (ACC-53,
 * David's ruling 2, 30 Oct: ~530 of 929 have never signed in, so "manage it
 * in your profile" is a dead end for them).
 *
 * ── THE SECOND ROUTE ABOVE THE AUTH GATE ────────────────────────────────────
 *
 * App.tsx matches it before the session, beside #/collect (REC-49), and it
 * keeps the same narrow rule: it never touches `Me`, never loads the shell,
 * and calls exactly one function, unsubscribe_by_token, which is anon by
 * design and returns the one word 'off'. The page says nothing about whose
 * link it is - not a name, not an address.
 *
 * ── OPENING IT CHANGES NOTHING ──────────────────────────────────────────────
 *
 * Safe Links and link scanners open mail links before a person does, so only
 * the press unsubscribes (Collect's rule, the magic-link lesson). The mail
 * client's own one-click unsubscribe is a POST to the unsubscribe Edge
 * Function, which is a different door to the same function.
 */
export function Unsubscribe({ token }: { token: string }) {
  const [lang, setLang] = useState<SignInLang>(() =>
    pickSignInLang(typeof navigator === 'undefined' ? [] : navigator.languages),
  )
  const [phase, setPhase] = useState<Phase>(() => (isUnsubscribeToken(token) ? 'ready' : 'bad'))

  useEffect(() => {
    document.documentElement.lang = SIGN_IN_LANGS.find((l) => l.key === lang)?.htmlLang ?? 'en'
  }, [lang])

  async function confirm() {
    if (phase === 'busy') return
    setPhase('busy')
    try {
      await unsubscribeByToken(token)
      setPhase('done')
    } catch (e) {
      setPhase((e as { code?: string }).code === '22023' ? 'bad' : 'failed')
    }
  }

  return (
    <main className="signin collect">
      <div className="signin__card collect__card">
        <img className="signin__logo" src={logo} alt="Conetec" width={140} />
        <UnsubscribeBody t={UNSUBSCRIBE[lang]} phase={phase} onConfirm={() => void confirm()} />
        <div className="signin__langs" role="group" aria-label={SIGN_IN_COPY[lang].languageLabel}>
          {SIGN_IN_LANGS.map((l) => (
            <button
              key={l.key}
              type="button"
              className="btn btn--link"
              lang={l.htmlLang}
              aria-pressed={lang === l.key}
              onClick={() => setLang(l.key)}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
    </main>
  )
}
